import {useCallback, useState} from "react";
import * as Sentry from "@sentry/react";
import {cloneDeep} from "lodash";
import {useKeycloakFunctions} from "./useKeycloakFunctions";
import {userAccountsAPI} from "../common/api/userAccountsAPI";
import {
    API_DIRECT_DEBIT_SETTINGS,
    API_DIRECT_DEBIT_SETTINGS_CANCEL,
    API_DIRECT_SALES,
    API_LEADS,
    API_POINTS_PLATFORM,
    API_POINTS_PLATFORM_CONFIGURE,
    API_USERS, API_USER_BANK_ACCOUNTS,
    API_USER_BOOKING_STATUS,
    API_USER_CONVERT_TRIAL,
    API_USER_ONLINE_ACCOUNT_CREATION,
    API_USER_ONLINE_ACCOUNT_VERIFICATION,
    API_USER_RESET_PASSWORD,
    API_USER_SETTINGS
} from "../common/api/apiConstants";
import {createURLQueryFromParams} from "../common/api/apiUtil";
import {GET_SALUTATION, DIRECT_DEBIT_EEA_COUNTRIES, DIRECT_DEBIT_STATUS} from "../common/constant";
import {dateToURLDateString} from "../common/date";

export const useUserAccounts = () => {

    const {initialized, authenticated, userId} = useKeycloakFunctions();

    const [user, setUser] = useState(undefined);
    const [loadingGetUser, setLoadingGetUser] = useState(false);
    const [errorGetUser, setErrorGetUser] = useState(false);

    const [updatedUser, setUpdatedUser] = useState(undefined);
    const [loadingUpdateUser, setLoadingUpdateUser] = useState(false);
    const [errorUpdateUser, setErrorUpdateUser] = useState(false);

    const [userSettings, setUserSettings] = useState(undefined);
    const [loadingGetUserSettings, setLoadingGetUserSettings] = useState(false);
    const [errorGetUserSettings, setErrorGetUserSettings] = useState(false);

    const [userBankAccounts, setUserBankAccounts] = useState(undefined);
    const [loadingGetUserBankAccounts, setLoadingGetUserBankAccounts] = useState(false);
    const [errorGetUserBankAccounts, setErrorGetUserBankAccounts] = useState(false);

    const [createdUserBankAccount, setCreatedUserBankAccount] = useState(undefined);
    const [loadingCreateUserBankAccount, setLoadingCreateUserBankAccount] = useState(false);
    const [errorCreateUserBankAccount, setErrorCreateUserBankAccount] = useState(false);

    const [isUserBankAccountConfigured, setIsUserBankAccountConfigured] = useState(undefined);
    const [loadingConfigureUserBankAccount, setLoadingConfigureUserBankAccount] = useState(false);
    const [errorConfigureUserBankAccount, setErrorConfigureUserBankAccount] = useState(false);

    const [isUserBankAccountDeleted, setIsUserBankAccountDeleted] = useState(false);
    const [loadingDeleteUserBankAccount, setLoadingDeleteUserBankAccount] = useState(false);
    const [errorDeleteUserBankAccount, setErrorDeleteUserBankAccount] = useState(false);

    const [updatedUserSettings, setUpdatedUserSettings] = useState(undefined);
    const [loadingUpdateUserSettings, setLoadingUpdateUserSettings] = useState(false);
    const [errorUpdateUserSettings, setErrorUpdateUserSettings] = useState(false);

    const [loadingUpdateUserPassword, setLoadingUpdateUserPassword] = useState(false);
    const [errorUpdateUserPassword, setErrorUpdateUserPassword] = useState(false);
    const [successUpdateUserPassword, setSuccessUpdateUserPassword] = useState(undefined);

    const [dataCreateLead, setDataCreateLead] = useState();
    const [loadingCreateLead, setLoadingCreateLead] = useState(false);
    const [errorCreateLead, setErrorCreateLead] = useState(null);

    const [convertTrial, setConvertTrial] = useState(undefined);
    const [loadingConvertTrial, setLoadingConvertTrial] = useState(false);
    const [errorConvertTrial, setErrorConvertTrial] = useState(false);

    const [userName, setUserName] = useState(undefined);
    const [loadingGetUserName, setLoadingGetUserName] = useState(false);
    const [errorGetUserName, setErrorGetUserName] = useState(false);

    const [directDebitSettings, setDirectDebitSettings] = useState();
    const [loadingDirectDebitSettings, setLoadingDirectDebitSettings] = useState();
    const [errorDirectDebitSettings, setErrorDirectDebitSettings] = useState();

    const [directDebitAccount, setDirectDebitAccount] = useState(undefined);
    const [loadingCreateDirectDebitBankAccount, setLoadingCreateDirectDebitBankAccount] = useState(false);
    const [errorCreateDirectDebitBankAccount, setErrorCreateDirectDebitBankAccount] = useState(null);

    const [loadingCancelDirectDebit, setLoadingCancelDirectDebit] = useState(false);
    const [errorCancelDirectDebit, setErrorCancelDirectDebit] = useState(null);

    const [bookingStatus, setBookingStatus] = useState(undefined);
    const [loadingBookingStatus, setLoadingBookingStatus] = useState(false);
    const [errorBookingStatus, setErrorBookingStatus] = useState(null);

    const [verificationResult, setVerificationResult] = useState(undefined);
    const [loadingVerificationResult, setLoadingVerificationResult] = useState(false);
    const [errorVerificationResult, setErrorVerificationResult] = useState(null);

    const [onlineAccountResult, setOnlineAccountResult] = useState(undefined);
    const [loadingOnlineAccountResult, setLoadingOnlineAccountResult] = useState(false);
    const [errorOnlineAccountResult, setErrorOnlineAccountResult] = useState(null);

    const [directSalesContract, setDirectSalesContract] = useState(undefined); // this is used also for direct sales checkout
    const [loadingDirectSalesContract, setLoadingDirectSalesContract] = useState(false);
    const [errorDirectSalesContract, setErrorDirectSalesContract] = useState(null);

    const [loadingDirectSalesCheckout, setLoadingDirectSalesCheckout] = useState(false);
    const [errorDirectSalesCheckout, setErrorDirectSalesCheckout] = useState(null);

    const getUser = useCallback((user_id = userId) => {

        if (initialized && authenticated) {
            setLoadingGetUser(true);
            setErrorGetUser(null);

            return userAccountsAPI.get(API_USERS + "/" + user_id)
                .then(res => {
                    setUser(res?.data);
                    return res;
                })
                .catch(err => {
                    setErrorGetUser(err.response);
                    throw err.response;
                })
                .finally(() => {
                    setLoadingGetUser(false);
                });
        } else {
            return new Promise(resolve => resolve());
        }
    }, [initialized, authenticated]);

    const updateUser = (fields, user_id = userId) => {
        // Salutation is linked to gender value, so when we change the Salutation we should also change the gender.
        const fieldsToSend = cloneDeep(fields);
        if ("salutation" in fieldsToSend) {
            fieldsToSend["gender"] = GET_SALUTATION[fieldsToSend.salutation]?.gender;
        }

        if (initialized && authenticated) {
            setLoadingUpdateUser(true);
            setErrorUpdateUser(null);

            userAccountsAPI.post(API_USERS + "/" + user_id, fieldsToSend)
                .then(res => res.data)
                .then(response => {
                    setLoadingUpdateUser(false);
                    const userResponse = {...user};
                    Object.keys(response).forEach((key) => {
                        userResponse[key] = response[key];
                    });
                    setUser(userResponse);
                    setUpdatedUser(response);
                })
                .catch(err => {
                    setLoadingUpdateUser(false);
                    setErrorUpdateUser(err.response);
                });
        }
    };

    const getUserSettings = useCallback((user_id = userId) => {
        if (initialized && authenticated) {
            setLoadingGetUserSettings(true);
            setErrorGetUserSettings(null);

            return userAccountsAPI.get(API_USERS + "/" + user_id + API_USER_SETTINGS)
                .then(res => {
                    setUserSettings(res?.data);
                    return res;
                })
                .catch(err => {
                    setErrorGetUserSettings(err.response);
                    throw err.response;
                })
                .finally(() => {
                    setLoadingGetUserSettings(false);
                });
        } else {
            return new Promise(resolve => resolve());
        }
    }, [initialized, authenticated]);

    const getUserBankAccounts = () => {
        if (initialized && authenticated) {
            setLoadingGetUserBankAccounts(true);
            setErrorGetUserBankAccounts(null);
            return userAccountsAPI.get(API_USER_BANK_ACCOUNTS)
                .then(res => res.data)
                .then(response => {
                    setLoadingGetUserBankAccounts(false);

                    const res = response?.map(a => {
                        let status;
                        switch (a?.direct_debit_status) {
                            case "captured":
                                status = DIRECT_DEBIT_STATUS.PENDING;
                                break;
                            case "active":
                                status = DIRECT_DEBIT_STATUS.ACTIVE;
                                break;
                            default:
                                status = "";
                                break;
                        }
                        a.direct_debit_status = status;
                        return a;
                    });

                    setUserBankAccounts(res);
                    return res;
                })
                .catch(err => {
                    setLoadingGetUserSettings(false);
                    setErrorGetUserBankAccounts(err);
                });
        } else {
            return new Promise(resolve => resolve());
        }
    };


    const updateUserSettings = (fields, user_id = userId) => {
        const fieldsToSend = cloneDeep(fields);

        if (initialized && authenticated) {
            setLoadingUpdateUserSettings(true);
            setErrorUpdateUserSettings(null);

            userAccountsAPI.post(API_USERS + "/" + user_id + API_USER_SETTINGS, fieldsToSend)
                .then(res => res.data)
                .then(response => {
                    setLoadingUpdateUserSettings(false);
                    const userSettingsResponse = {...userSettings};
                    Object.keys(response).forEach((key) => {
                        userSettingsResponse[key] = response[key];
                    });
                    setUserSettings(userSettingsResponse);
                    setUpdatedUserSettings(response);
                })
                .catch(err => {
                    setLoadingUpdateUserSettings(false);
                    setErrorUpdateUserSettings(err.response.data);
                });
        }
    };

    const updateUserPassword = (fields, user_id = userId) => {
        const fieldsToSend = cloneDeep(fields);
        if (initialized && authenticated) {
            setLoadingUpdateUserPassword(true);
            setErrorUpdateUserPassword(null);

            userAccountsAPI.post(API_USERS + "/" + user_id + API_USER_RESET_PASSWORD, fieldsToSend)
                .then(res => res.data)
                .then(() => {
                    setLoadingUpdateUserPassword(false);
                    setSuccessUpdateUserPassword(true);
                })
                .catch(err => {
                    setLoadingUpdateUserPassword(false);
                    setErrorUpdateUserPassword(err.response);
                });
        }
    };

    const createUserBankAccount = (body, configure = false) => {
        if (initialized && authenticated) {
            setLoadingCreateUserBankAccount(true);
            setErrorCreateUserBankAccount(null);
            userAccountsAPI.post(API_USER_BANK_ACCOUNTS, body)
                .then(res => res.data)
                .then(response => {
                    setCreatedUserBankAccount(response);
                    if (configure) {
                        configureUserBankAccount(body);
                    }
                })
                .catch(err => {
                    setErrorCreateUserBankAccount(err);
                })
                .finally(() => {
                    setLoadingCreateUserBankAccount(false);
                });
        }
    };

    const configureUserBankAccount = (body) => {
        if (initialized && authenticated) {
            setLoadingConfigureUserBankAccount(true);
            setErrorConfigureUserBankAccount(null);
            userAccountsAPI.post(API_POINTS_PLATFORM + API_USER_BANK_ACCOUNTS + API_POINTS_PLATFORM_CONFIGURE, body)
                .then(() => {
                    setLoadingConfigureUserBankAccount(false);
                    setIsUserBankAccountConfigured(true);
                })
                .catch(err => {
                    setLoadingConfigureUserBankAccount(false);
                    setErrorConfigureUserBankAccount(err);
                });
        }
    };

    const deleteUserBankAccount = (iban) => {
        if (initialized && authenticated) {
            setLoadingDeleteUserBankAccount(true);
            setIsUserBankAccountDeleted(false);
            setErrorDeleteUserBankAccount(null);
            userAccountsAPI.delete(API_USER_BANK_ACCOUNTS + "/" + iban)
                .then(() => {
                    setLoadingDeleteUserBankAccount(false);
                    setIsUserBankAccountDeleted(true);
                })
                .catch(err => {
                    setLoadingDeleteUserBankAccount(false);
                    setErrorDeleteUserBankAccount(err);
                });
        }
    };

    const createLead = (body) => {
        setLoadingCreateLead(true);
        setErrorCreateLead(null);

        return userAccountsAPI.post(API_LEADS, body)
            .then(res => {
                setDataCreateLead(res.data);
                return res;
            })
            .catch(err => {
                setErrorCreateLead(err?.response);
                throw err.response;
            })
            .finally(() => {
                setLoadingCreateLead(false);
            });
    };

    const convertUserToTrial = () => {
        if (initialized && authenticated) {
            setLoadingConvertTrial(true);
            setErrorConvertTrial(null);

            return userAccountsAPI.put(API_USERS + API_USER_CONVERT_TRIAL)
                .then(res => res.data)
                .then(response => {
                    // Add 3 seconds delay for SAP to sync with Keycloak
                    setTimeout(() => {
                        setLoadingConvertTrial(false);
                        setConvertTrial(response);
                        setErrorConvertTrial(null);
                    }, 3000);
                    return response;
                })
                .catch(err => {
                    setLoadingConvertTrial(false);
                    setErrorConvertTrial(err.response);
                    throw err.response;
                });
        } else {
            return new Promise(reject => reject());
        }
    };

    const getUserNameByMemberId = useCallback((member_id) => {
        if (initialized && authenticated) {
            setLoadingGetUserName(true);
            setErrorGetUserName(null);

            return userAccountsAPI.get(API_POINTS_PLATFORM + API_USERS + createURLQueryFromParams({member_id}))
                .then(res => {
                    setUserName(res?.data);
                    return res;
                })
                .catch(err => {
                    setErrorGetUserName(err);
                    throw err.response;
                })
                .finally(() => {
                    setLoadingGetUserName(false);
                });
        } else {
            return new Promise(resolve => resolve());
        }
    }, [initialized, authenticated]);

    const getDirectDebitSettings = useCallback((userCountry) => {
        const isDirectDebitAvailable = DIRECT_DEBIT_EEA_COUNTRIES?.some(country => country === userCountry?.toUpperCase());
        if (isDirectDebitAvailable && initialized && authenticated) {
            setLoadingDirectDebitSettings(true);
            setErrorDirectDebitSettings(null);

            return userAccountsAPI.get(API_USER_BANK_ACCOUNTS + API_DIRECT_DEBIT_SETTINGS)
                .then(res => {
                    switch (res?.data?.status) {
                        case "captured":
                            res.data.status = DIRECT_DEBIT_STATUS.PENDING;
                            break;
                        case "active":
                            res.data.status = DIRECT_DEBIT_STATUS.ACTIVE;
                            break;
                        default:
                            res.data.status = DIRECT_DEBIT_STATUS.INACTIVE;
                            break;
                    }
                    setDirectDebitSettings(res?.data);
                    return res;
                })
                .catch(err => {
                    if (err.response.status === 404 && err.response.data?.detail?.msg === "direct_debit_settings_not_found") {
                        const data = {
                            "mandate_number": "",
                            "iban": "",
                            "account_holder_name": "",
                            "bank_name": "",
                            "bank_code": "",
                            "bank_swift_code": "",
                            "bank_location": "",
                            "bank_country": "",
                            "status": DIRECT_DEBIT_STATUS.INACTIVE
                        };
                        setDirectDebitSettings(data);
                        return data;
                    } else {
                        setErrorDirectDebitSettings(err);
                        Sentry.captureException(err.response);
                        throw err.response;
                    }
                })
                .finally(() => {
                    setLoadingDirectDebitSettings(false);
                });
        }
    }, [initialized, authenticated]);

    const createDirectDebitBankAccount = useCallback((data) => {
        if (initialized && authenticated) {
            setLoadingCreateDirectDebitBankAccount(true);
            setErrorCreateDirectDebitBankAccount(null);

            userAccountsAPI.post(API_USER_BANK_ACCOUNTS + API_DIRECT_DEBIT_SETTINGS, data)
                .then((res) => {
                    setDirectDebitAccount(res?.data);
                })
                .catch((err) => {
                    console.error(err.response);
                    setErrorCreateDirectDebitBankAccount(err);
                    throw err.response;
                })
                .finally(() => {
                    setLoadingCreateDirectDebitBankAccount(false);
                });
        }
    }, [initialized, authenticated]);

    const cancelDirectDebit = useCallback((iban) => {
        if (initialized && authenticated) {
            setLoadingCancelDirectDebit(true);
            setErrorCancelDirectDebit(null);

            const body = {
                "iban": iban
            };

            return userAccountsAPI.post(API_USER_BANK_ACCOUNTS + API_DIRECT_DEBIT_SETTINGS_CANCEL, body)
                .then(res => {
                    switch (res?.data?.status) {
                        case "captured":
                            res.data.status = DIRECT_DEBIT_STATUS.PENDING;
                            break;
                        case "active":
                            res.data.status = DIRECT_DEBIT_STATUS.ACTIVE;
                            break;
                        default:
                            res.data.status = DIRECT_DEBIT_STATUS.INACTIVE;
                            break;
                    }
                    setDirectDebitSettings(res?.data);
                    return res;
                })
                .catch(err => {
                    setErrorCancelDirectDebit(err?.response);
                    throw err.response;
                })
                .finally(() => {
                    setLoadingCancelDirectDebit(false);
                });
        }
    }, [initialized, authenticated]);

    const getBookingStatus = useCallback((user_id = userId) => {
        if (initialized && authenticated) {
            setLoadingBookingStatus(true);
            setErrorBookingStatus(null);

            return userAccountsAPI.get(API_USERS + "/" + user_id + API_USER_BOOKING_STATUS)
                .then(res => res.data)
                .then(response => {
                    setBookingStatus(response);
                    return response;
                })
                .catch(err => {
                    setErrorBookingStatus(err);
                })
                .finally(() => {
                    setLoadingBookingStatus(false);
                });
        }
    }, [initialized, authenticated]);

    const verifyUser = (userData, recaptchaToken) => {
        setLoadingVerificationResult(true);
        setErrorVerificationResult(null);
        const payload = {
            member_id: userData.member_id,
            email: userData.email,
            first_name: userData.first_name,
            last_name: userData.last_name,
            date_of_birth: dateToURLDateString(userData.date_of_birth),
            product_number: userData.product_number,
            recaptcha_token: recaptchaToken
        };

        return userAccountsAPI.post(API_USERS + API_USER_ONLINE_ACCOUNT_VERIFICATION, payload)
            .then(res => res.data)
            .then(response => {
                setVerificationResult(response);
            })
            .catch(err => {
                setErrorVerificationResult(err.response);
            })
            .finally(() => {
                setLoadingVerificationResult(false);
            });
    };

    const createOnlineAccount = (userData) => {
        setLoadingOnlineAccountResult(true);
        setErrorOnlineAccountResult(null);

        const payload = {
            ...userData,
            member_id: userData.member_id,
            email: userData.email,
            date_of_birth: dateToURLDateString(userData.date_of_birth),
        };

        return userAccountsAPI.post(API_USERS + API_USER_ONLINE_ACCOUNT_CREATION, payload)
            .then(res => res.data)
            .then(response => {
                setOnlineAccountResult(response);
            })
            .catch(err => {
                setErrorOnlineAccountResult(err.response?.data);
            })
            .finally(() =>{
                setLoadingOnlineAccountResult(false);
            });
    };

    const getUserDirectSalesContract = useCallback((flowSource, number_of_shares, signature, create_opportunity = false) => {
        if (initialized && authenticated) {
            setLoadingDirectSalesContract(true);
            setErrorDirectSalesContract(null);

            if (signature) {
                userAccountsAPI.defaults.headers["x-ds-signature"] = signature;
            }

            return userAccountsAPI.get(`${API_DIRECT_SALES}/contract?flow_source=${flowSource}${number_of_shares ? `&number_of_shares=${number_of_shares}` : ""}${create_opportunity ? `&create_opportunity=${create_opportunity}` : ""}`, {
                responseType: "blob"
            })
                .then(res => {
                    setDirectSalesContract(res?.data);
                    return res;
                })
                .catch(err => {
                    setErrorDirectSalesContract(err);
                    throw err;
                })
                .finally(() => {
                    setLoadingDirectSalesContract(false);
                });
        }
    }, [initialized, authenticated]);

    const doDirectSalesCheckout = useCallback((numberOfShares) => {
        if (initialized && authenticated) {
            setLoadingDirectSalesCheckout(true);
            setErrorDirectSalesCheckout(null);

            return userAccountsAPI.post(`${API_DIRECT_SALES}/checkout?number_of_shares=${numberOfShares}`, {}, {
                responseType: "blob"
            })
                .then(res => {
                    setDirectSalesContract(res?.data);
                    return res;
                })
                .catch(err => {
                    setErrorDirectSalesCheckout(err);
                    throw err;
                })
                .finally(() => {
                    setLoadingDirectSalesCheckout(false);
                });
        }
    }, [initialized, authenticated]);

    return {
        getUser,
        user,
        loadingGetUser,
        errorGetUser,

        updateUser,
        updatedUser,
        loadingUpdateUser,
        errorUpdateUser,

        getUserSettings,
        userSettings,
        loadingGetUserSettings,
        errorGetUserSettings,

        getUserBankAccounts,
        userBankAccounts,
        loadingGetUserBankAccounts,
        errorGetUserBankAccounts,

        createUserBankAccount,
        createdUserBankAccount,
        loadingCreateUserBankAccount,
        errorCreateUserBankAccount,

        isUserBankAccountConfigured,
        configureUserBankAccount,
        loadingConfigureUserBankAccount,
        errorConfigureUserBankAccount,

        isUserBankAccountDeleted,
        deleteUserBankAccount,
        loadingDeleteUserBankAccount,
        errorDeleteUserBankAccount,

        updateUserSettings,
        updatedUserSettings,
        loadingUpdateUserSettings,
        errorUpdateUserSettings,

        updateUserPassword,
        setSuccessUpdateUserPassword,
        loadingUpdateUserPassword,
        successUpdateUserPassword,
        errorUpdateUserPassword,

        createLead,
        dataCreateLead,
        loadingCreateLead,
        errorCreateLead,

        convertUserToTrial,
        convertTrial,
        loadingConvertTrial,
        errorConvertTrial,

        userName,
        loadingGetUserName,
        errorGetUserName,
        getUserNameByMemberId,

        directDebitSettings,
        loadingDirectDebitSettings,
        errorDirectDebitSettings,
        getDirectDebitSettings,

        createDirectDebitBankAccount,
        directDebitAccount,
        loadingCreateDirectDebitBankAccount,
        errorCreateDirectDebitBankAccount,

        cancelDirectDebit,
        loadingCancelDirectDebit,
        errorCancelDirectDebit,

        getBookingStatus,
        bookingStatus,
        loadingBookingStatus,
        errorBookingStatus,

        verifyUser,
        verificationResult,
        loadingVerificationResult,
        errorVerificationResult,

        createOnlineAccount,
        onlineAccountResult,
        loadingOnlineAccountResult,
        errorOnlineAccountResult,

        getUserDirectSalesContract,
        directSalesContract,
        loadingDirectSalesContract,
        errorDirectSalesContract,
        setLoadingDirectSalesContract,

        doDirectSalesCheckout,
        loadingDirectSalesCheckout,
        errorDirectSalesCheckout,
    };
};
